import React from "react";
import DropDown from "../search/DropDown";
import FilterSheet from "./FilterSheet";
import useIsPhone from "./useIsPhone";

/**
 * Filter bar for /image-search.
 *
 * Reuses /search's DropDown so the interaction is the one users already know.
 * That component is purely presentational — the Algolia coupling on /search
 * lives in the RefinementList passed as its child, not in the dropdown itself.
 *
 * Unlike /search, this is visible at ALL widths. /search wraps its filter row
 * in `d-none d-md-block`, so phone users get no filters at all; image search is
 * phone-first (photograph a thing, search it), so inheriting that would defeat
 * the feature for its most likely users.
 */

const RADII = [5, 10, 25, 50, 100];

function CheckList({ options, selected, onToggle, fluid }) {
    const [query, setQuery] = React.useState("");
    const term = query.trim().toLowerCase();
    const shown = term
        ? options.filter((o) => (o.title || o).toLowerCase().includes(term))
        : options;

    // The 240px floor is what makes this fit a desktop popover. In a bottom
    // sheet it would force horizontal overflow on a 390px screen, so the sheet
    // sizes itself to the viewport instead.
    return (
        <div style={fluid ? null : { minWidth: "240px" }}>
            {options.length > 12 && (
                <input
                    type="search"
                    className="form-control form-control-sm mb-2"
                    placeholder="Search…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
            )}
            <div style={{ maxHeight: "260px", overflowY: "auto" }}>
                {shown.length === 0 && (
                    <p className="small text-muted mb-0 px-1">No matches</p>
                )}
                {shown.map((option) => {
                    const value = option.id !== undefined ? option.id : option;
                    const label = option.title !== undefined ? option.title : option;
                    const id = "f-" + String(value).replace(/\W/g, "");

                    return (
                        <div className="custom-control custom-checkbox py-1" key={value}>
                            <input
                                type="checkbox"
                                className="custom-control-input"
                                id={id}
                                checked={selected.includes(value)}
                                onChange={() => onToggle(value)}
                            />
                            <label className="custom-control-label small" htmlFor={id}>
                                {label}
                            </label>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default function Filters({ facets, filters, onChange, disabled, locked }) {
    const isPhone = useIsPhone();
    const [openSheet, setOpenSheet] = React.useState(null);
    const set = (patch) => onChange(Object.assign({}, filters, patch));

    const toggle = (key, value) => {
        const current = filters[key] || [];
        set({
            [key]: current.includes(value)
                ? current.filter((v) => v !== value)
                : current.concat([value]),
        });
    };

    // A count in the label is why DropDown had to stop snapshotting `label`
    // into state — it never updated when the prop changed.
    const count = (key) => {
        const value = filters[key];
        if (Array.isArray(value)) return value.length ? ` (${value.length})` : "";
        return value ? " (1)" : "";
    };

    const chips = [];
    (filters.category_ids || []).forEach((id) => {
        const match = facets.categories.find((c) => c.id === id);
        if (match) chips.push({ label: match.title, clear: () => toggle("category_ids", id) });
    });
    (filters.permit_ids || []).forEach((id) => {
        const match = facets.permits.find((p) => p.id === id);
        if (match) chips.push({ label: match.title, clear: () => toggle("permit_ids", id) });
    });
    // "in Malibu" rather than bare "Malibu": these chips are also how a
    // place word typed into the text search renders (it IS the same filter
    // state), and the preposition makes that reading unambiguous.
    if (filters.city) chips.push({ label: "in " + filters.city, clear: () => set({ city: "" }) });
    if (filters.county) chips.push({ label: "in " + filters.county, clear: () => set({ county: "" }) });
    if (filters.near && filters.radius) {
        chips.push({
            label: `within ${filters.radius} mi of ${filters.near}`,
            clear: () => set({ near: "", radius: "" }),
        });
    }

    // `disabled` fades the bar while a search runs but leaves it INTERACTIVE.
    // pointerEvents:none here would stop you refining filters mid-request, and
    // the debounce plus requestGen already make a superseded response harmless.
    // Described once, presented two ways: a popover on desktop, a bottom sheet
    // on phones. Keeping one source for the panel contents means the two
    // presentations cannot drift into offering different filters.
    const panels = [
        {
            key: "categories",
            label: "Categories" + count("category_ids"),
            render: (fluid) => (
                <CheckList
                    fluid={fluid}
                    options={facets.categories}
                    selected={filters.category_ids || []}
                    onToggle={(id) => toggle("category_ids", id)}
                />
            ),
        },
        {
            key: "permits",
            label: "Permits" + count("permit_ids"),
            render: (fluid) => (
                <CheckList
                    fluid={fluid}
                    options={facets.permits}
                    selected={filters.permit_ids || []}
                    onToggle={(id) => toggle("permit_ids", id)}
                />
            ),
        },
        {
            key: "city",
            label: "City" + count("city"),
            render: (fluid) => (
                <CheckList
                    fluid={fluid}
                    options={facets.cities}
                    selected={filters.city ? [filters.city] : []}
                    onToggle={(city) => set({ city: filters.city === city ? "" : city })}
                />
            ),
        },
        {
            key: "county",
            label: "County" + count("county"),
            render: (fluid) => (
                <CheckList
                    fluid={fluid}
                    options={facets.counties}
                    selected={filters.county ? [filters.county] : []}
                    onToggle={(county) => set({ county: filters.county === county ? "" : county })}
                />
            ),
        },
        {
            key: "distance",
            label: "Distance" + (filters.near && filters.radius ? " (1)" : ""),
            render: (fluid) => (
                <div style={fluid ? null : { minWidth: "260px" }}>
                    <label className="small mb-1">Near a city or zip</label>
                    <input
                        type="text"
                        className="form-control form-control-sm mb-2"
                        placeholder="e.g. Pasadena or 91101"
                        value={filters.near || ""}
                        onChange={(e) => set({ near: e.target.value })}
                        list="il-place-list"
                    />
                    <datalist id="il-place-list">
                        {facets.cities.map((c) => (
                            <option key={c} value={c} />
                        ))}
                    </datalist>

                    <label className="small mb-1">Within</label>
                    <select
                        className="form-control form-control-sm"
                        value={filters.radius || ""}
                        onChange={(e) => set({ radius: e.target.value })}
                    >
                        <option value="">Any distance</option>
                        {RADII.map((r) => (
                            <option key={r} value={r}>
                                {r} miles
                            </option>
                        ))}
                    </select>

                    {facets.without_coordinates > 0 && (
                        // The limitation is real — a record with no coordinates
                        // is excluded by the filter — but the COUNT is not the
                        // user's problem and reads as an apology for the data.
                        <p className="small text-muted mt-2 mb-0">
                            Locations without a map position won’t appear in
                            distance searches.
                        </p>
                    )}
                </div>
            ),
        },
    ];

    const openPanel = panels.find((p) => p.key === openSheet);

    // `locked` is a different animal from `disabled`: disabled fades during a
    // request but stays interactive (see below); locked means there is no
    // search these controls could run — a replayed upload's file is gone — so
    // the whole bar goes inert rather than rendering silently dead controls.
    if (locked) {
        return (
            <div
                className="il-image-search-filters mb-3"
                style={{ opacity: 0.5, pointerEvents: 'none' }}
                aria-disabled="true"
                title="Filters can't be changed on a replayed upload — start a new search to refine"
            >
                <div className={"d-flex align-items-center " + (isPhone ? "il-filter-chip-row" : "flex-wrap")}>
                    {panels.map((p) => (
                        <span key={p.key} className="il-btn-trigger btn btn-sm mr-3 text-muted">
                            <span className="il-drop-down-label">{p.label}</span>
                        </span>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="il-image-search-filters mb-3" style={disabled ? { opacity: 0.6 } : null}>
            {/* On a phone the five chips wrap onto a second row, which doubles
                the height of a bar that is now sticky. Scroll them sideways
                instead. `flex-wrap` is simply not applied here rather than
                overridden — Bootstrap's .flex-wrap is `!important` and would
                otherwise need another !important to beat it. */}
            <div className={"d-flex align-items-center " + (isPhone ? "il-filter-chip-row" : "flex-wrap")}>
                {isPhone
                    ? panels.map((p) => (
                          <button
                              key={p.key}
                              type="button"
                              className="il-filter-chip"
                              aria-haspopup="dialog"
                              aria-expanded={openSheet === p.key}
                              onClick={() => setOpenSheet(p.key)}
                          >
                              {p.label}
                          </button>
                      ))
                    : panels.map((p) => (
                          <DropDown key={p.key} label={p.label}>
                              {p.render(false)}
                          </DropDown>
                      ))}
            </div>

            {/* One sheet at a time by construction — the open filter is a single
                piece of state, so a second cannot be opened alongside it. */}
            {isPhone && openPanel && (
                <FilterSheet title={openPanel.label} onClose={() => setOpenSheet(null)}>
                    {openPanel.render(true)}
                </FilterSheet>
            )}

            {chips.length > 0 && (
                <div className="d-flex flex-wrap align-items-center mt-2">
                    {chips.map((chip, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={chip.clear}
                            className="btn btn-sm btn-light border mr-2 mb-1"
                        >
                            {chip.label} <span aria-hidden="true">×</span>
                            <span className="sr-only">Remove filter</span>
                        </button>
                    ))}
                    <button
                        type="button"
                        className="btn btn-sm btn-link mb-1"
                        onClick={() =>
                            onChange({ category_ids: [], permit_ids: [], city: "", county: "", near: "", radius: "" })
                        }
                    >
                        Clear all
                    </button>
                </div>
            )}
        </div>
    );
}
